// Shared by the Cloudflare gallery function and the client-side router, so a
// screen cannot be non-indexable on first load and indexable after a
// client-side navigation. Keep this module free of DOM and Node types: the
// Workers build compiles it without the DOM lib.

export const PLATFORM_LABELS: Record<string, string> = {
    u8g2: 'U8g2',
    'tft-espi': 'TFT_eSPI',
    adafruit_gfx: 'Adafruit GFX (color)',
    adafruit_gfx_mono: 'Adafruit GFX (monochrome)',
    arduino_gfx: 'Arduino GFX',
    lvgl: 'LVGL',
    esphome: 'ESPHome',
    micropython: 'MicroPython',
    circuitpython: 'CircuitPython',
    flipper: 'Flipper Zero',
    gxepd2: 'GxEPD2 e-paper',
    'free-drawing': 'Free Drawing',
};

export function platformLabel(platform: string): string {
    return PLATFORM_LABELS[platform] ?? platform;
}

/**
 * Titles Lopaka assigns automatically. More than half of all public projects
 * still carry one, and a page whose only real text is "Project 1" gives a
 * search engine nothing to index or rank.
 */
// The project wizard proposes "Project N" in the app language (projects.wizard.defaultTitle).
const AUTO_TITLE = /^\s*(project|proyecto|projekt|screen|untitled|new project|test)\s*\d*\s*(\(copy\))?\s*$/i;

export function hasAuthoredTitle(title: string | null | undefined): boolean {
    const trimmed = (title ?? '').trim();
    return trimmed.length > 2 && !AUTO_TITLE.test(trimmed);
}

export interface GalleryScreenFacts {
    projectTitle: string;
    screenTitle?: string | null;
    platform: string;
    screenWidth: number;
    screenHeight: number;
}

export function galleryScreenTitle(facts: GalleryScreenFacts): string {
    const screen = (facts.screenTitle ?? '').trim();
    const base = hasAuthoredTitle(screen) ? `${facts.projectTitle}: ${screen}` : facts.projectTitle;
    return `${base} — ${platformLabel(facts.platform)} UI on a ${facts.screenWidth}×${facts.screenHeight} display`;
}

export function galleryScreenDescription(facts: GalleryScreenFacts): string {
    const screen = (facts.screenTitle ?? '').trim();
    const prefix = hasAuthoredTitle(screen) ? `${facts.projectTitle} — ${screen}. ` : `${facts.projectTitle}. `;
    return `${prefix}A ${platformLabel(facts.platform)} interface designed for a ${facts.screenWidth}×${facts.screenHeight}px display. Open it in the Lopaka editor to inspect the layers and generate the display code.`;
}

export function isIndexableScreen(projectTitle: string | null | undefined, previewUrl: string | null | undefined) {
    return hasAuthoredTitle(projectTitle) && Boolean(previewUrl);
}
